import type { ConversationSort } from "@redotech/effect-api-shared/schema/query-options";
import {
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { composeWrappers } from "@redotech/react-util/component";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import {
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import {
  decodeAdvancedFilterGroup,
  encodeAdvancedFilterGroup,
} from "@redotech/redo-web/advanced-filters/advanced-filter";
import {
  createContext,
  Dispatch,
  memo,
  ReactElement,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSupportTicketAdvancedFilters } from "./get-support-ticket-advanced-filters";

const URL_FILTER_GROUP_PARAM = "fg";

// ============================================================================
// Type-Safe Query Options for Conversations
// ============================================================================

export interface SupportTicketQueryOptions {
  filterGroup: FilterGroup;
  sorts: ConversationSort[];
  limit?: number;
}

// ============================================================================
// Default Query Options
// ============================================================================

const DEFAULT_STATUS_FILTER: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN"],
};

export const DEFAULT_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [DEFAULT_STATUS_FILTER],
};

const DEFAULT_SORT: ConversationSort = {
  kind: "lastCustomerMessageAt",
  direction: "desc",
  nulls: "last",
};

const DEFAULT_QUERY_OPTIONS: SupportTicketQueryOptions = {
  filterGroup: DEFAULT_FILTER_GROUP,
  sorts: [DEFAULT_SORT],
};

// ============================================================================
// Contexts
// ============================================================================

export const QueryOptionsContext = createContext<SupportTicketQueryOptions>(
  DEFAULT_QUERY_OPTIONS,
);

export const SetQueryOptionsContext = createContext<
  Dispatch<SetStateAction<SupportTicketQueryOptions>>
>(() => {});

// ============================================================================
// URL Utilities
// ============================================================================

function parseQueryOptionsFromUrl(search: string): SupportTicketQueryOptions {
  const queryParams = new URLSearchParams(search);
  const fgParam = queryParams.get(URL_FILTER_GROUP_PARAM);

  if (!fgParam) {
    return DEFAULT_QUERY_OPTIONS;
  }

  try {
    const advancedFilters = getSupportTicketAdvancedFilters();
    const filterGroup = decodeAdvancedFilterGroup(fgParam, advancedFilters);

    if (!filterGroup) {
      return DEFAULT_QUERY_OPTIONS;
    }

    return { filterGroup, sorts: DEFAULT_QUERY_OPTIONS.sorts };
  } catch {
    return DEFAULT_QUERY_OPTIONS;
  }
}

function filterGroupsAreEqual(a: FilterGroup, b: FilterGroup): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function queryOptionsAreEqual(
  a: SupportTicketQueryOptions,
  b: SupportTicketQueryOptions,
): boolean {
  return (
    filterGroupsAreEqual(a.filterGroup, b.filterGroup) &&
    JSON.stringify(a.sorts) === JSON.stringify(b.sorts)
  );
}

function buildUrlWithQueryOptions(
  currentSearch: string,
  queryOptions: SupportTicketQueryOptions,
): string {
  const searchParams = new URLSearchParams(currentSearch);

  if (queryOptionsAreEqual(queryOptions, DEFAULT_QUERY_OPTIONS)) {
    searchParams.delete(URL_FILTER_GROUP_PARAM);
  } else {
    const encoded = encodeAdvancedFilterGroup(queryOptions.filterGroup);
    searchParams.set(URL_FILTER_GROUP_PARAM, encoded);
  }

  // Once the user changes filters, the defaultView param is no longer accurate
  searchParams.delete("defaultView");

  const newSearch = searchParams.toString();
  return newSearch ? `?${newSearch}` : "";
}

function encodeFilterGroupForRequest(
  queryOptions: SupportTicketQueryOptions,
): string {
  return encodeAdvancedFilterGroup(queryOptions.filterGroup);
}

// ============================================================================
// Provider Component
// ============================================================================

export const SupportTicketFiltersProvider = memo(
  function SupportTicketFiltersProvider({
    children,
  }: {
    children: ReactElement;
  }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [queryOptions, setQueryOptionsState] =
      useState<SupportTicketQueryOptions>(() =>
        parseQueryOptionsFromUrl(location.search),
      );

    // When the URL changes externally (back/forward navigation, sidebar link),
    // reset state to match. We compare the parsed filter groups rather than
    // the encoded URL strings, since encode/decode is not idempotent.
    const urlQueryOptions = parseQueryOptionsFromUrl(location.search);
    if (!queryOptionsAreEqual(urlQueryOptions, queryOptions)) {
      setQueryOptionsState(urlQueryOptions);
    }

    const setQueryOptions: Dispatch<SetStateAction<SupportTicketQueryOptions>> =
      useCallback(
        (action) => {
          setQueryOptionsState((prev) => {
            const newOptions =
              typeof action === "function" ? action(prev) : action;
            const newSearch = buildUrlWithQueryOptions(
              location.search,
              newOptions,
            );
            navigate({ search: newSearch }, { replace: true });
            return newOptions;
          });
        },
        [location.search, navigate],
      );

    return composeWrappers(
      (inner) => (
        <QueryOptionsContext.Provider value={queryOptions}>
          {inner}
        </QueryOptionsContext.Provider>
      ),
      (inner) => (
        <SetQueryOptionsContext.Provider value={setQueryOptions}>
          {inner}
        </SetQueryOptionsContext.Provider>
      ),
    )(children);
  },
);

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access the current query options (filterGroup, sorts, limit)
 */
export function useQueryOptions(): SupportTicketQueryOptions {
  return useContext(QueryOptionsContext);
}

/**
 * Encodes the current support-ticket filters for server-side list/search.
 * This intentionally includes the default OPEN filter even when the URL omits
 * `fg`, so server pagination and local TanStack filtering stay aligned.
 */
export function useEncodedSupportTicketFilterGroup(): string {
  const queryOptions = useContext(QueryOptionsContext);
  return useMemo(
    () => encodeFilterGroupForRequest(queryOptions),
    [queryOptions],
  );
}

/**
 * Hook to access and set query options
 */
export function useQueryOptionsState(): {
  queryOptions: SupportTicketQueryOptions;
  setQueryOptions: Dispatch<SetStateAction<SupportTicketQueryOptions>>;
} {
  const queryOptions = useContext(QueryOptionsContext);
  const setQueryOptions = useContext(SetQueryOptionsContext);
  return { queryOptions, setQueryOptions };
}

/**
 * Hook to reset query options to defaults
 */
export function useResetFilters(): () => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(() => {
    setQueryOptions(DEFAULT_QUERY_OPTIONS);
  }, [setQueryOptions]);
}

/**
 * Hook to check if filters differ from defaults (for "Reset filters" button visibility)
 */
export function useHasActiveFilters(): boolean {
  const queryOptions = useContext(QueryOptionsContext);

  return useMemo(
    () => !queryOptionsAreEqual(queryOptions, DEFAULT_QUERY_OPTIONS),
    [queryOptions],
  );
}

// ============================================================================
// Filter Helper Hooks
// ============================================================================

/**
 * Helper to get leaf filters from a FilterGroup (flattens nested groups)
 */
function getLeafFilters(group: FilterGroup): AdvancedFilterData[] {
  const filters: AdvancedFilterData[] = [];
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      filters.push(...getLeafFilters(clause));
    } else {
      filters.push(clause);
    }
  }
  return filters;
}

/**
 * Hook to get/set the status filter specifically.
 * This is a common filter so it gets its own hook.
 */
export function useStatusFilter(): {
  status: string | null;
  setStatus: (status: string | null) => void;
} {
  const { queryOptions, setQueryOptions } = useQueryOptionsState();

  const statusFilter = useMemo(() => {
    const filters = getLeafFilters(queryOptions.filterGroup);
    return filters.find(
      (f): f is ArrayStringFilterData =>
        f.name === SupportTicketFilterNames.STATUS,
    );
  }, [queryOptions.filterGroup]);

  const status = useMemo(() => {
    if (
      !statusFilter ||
      !statusFilter.value ||
      statusFilter.value.length === 0
    ) {
      return null;
    }
    return statusFilter.value[0] ?? null;
  }, [statusFilter]);

  const setStatus = useCallback(
    (newStatus: string | null) => {
      setQueryOptions((prev) => {
        const otherFilters = getLeafFilters(prev.filterGroup).filter(
          (f) => f.name !== SupportTicketFilterNames.STATUS,
        );
        if (newStatus === null) {
          return {
            ...prev,
            filterGroup: {
              operator: FilterOperator.AND,
              clauses: otherFilters,
            },
          };
        }
        const newFilter: ArrayStringFilterData = {
          ...DEFAULT_STATUS_FILTER,
          value: [newStatus],
        };
        return {
          ...prev,
          filterGroup: {
            operator: FilterOperator.AND,
            clauses: [...otherFilters, newFilter],
          },
        };
      });
    },
    [setQueryOptions],
  );

  return { status, setStatus };
}

/**
 * Hook to manage sort state for a specific column.
 * Returns the current sort direction and a function to toggle it.
 * Cycle: none -> desc -> asc -> none
 */
export function useColumnSort(sortKind: ConversationSort["kind"]): {
  sortDirection: "asc" | "desc" | null;
  toggleSort: () => void;
} {
  const { queryOptions, setQueryOptions } = useQueryOptionsState();

  const sortDirection = useMemo((): "asc" | "desc" | null => {
    const sort = queryOptions.sorts.find((s) => s.kind === sortKind);
    return sort?.direction ?? null;
  }, [queryOptions.sorts, sortKind]);

  const toggleSort = useCallback(() => {
    setQueryOptions((prev) => {
      const otherSorts = prev.sorts.filter((s) => s.kind !== sortKind);

      let newSorts: ConversationSort[];
      switch (sortDirection) {
        case null:
          newSorts = [
            { kind: sortKind, direction: "desc", nulls: "last" },
            ...otherSorts,
          ];
          break;
        case "desc":
          newSorts = [
            { kind: sortKind, direction: "asc", nulls: "last" },
            ...otherSorts,
          ];
          break;
        case "asc":
          newSorts = otherSorts;
          break;
      }

      return { ...prev, sorts: newSorts };
    });
  }, [setQueryOptions, sortKind, sortDirection]);

  return { sortDirection, toggleSort };
}

/**
 * Hook to add a filter to the filter group
 */
export function useAddFilter(): (filter: AdvancedFilterData) => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(
    (filter: AdvancedFilterData) => {
      setQueryOptions((prev) => ({
        ...prev,
        filterGroup: {
          ...prev.filterGroup,
          clauses: [...prev.filterGroup.clauses, filter],
        },
      }));
    },
    [setQueryOptions],
  );
}

/**
 * Hook to remove a filter by name
 */
export function useRemoveFilter(): (filterName: string) => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(
    (filterName: string) => {
      setQueryOptions((prev) => {
        const newClauses = prev.filterGroup.clauses.filter((clause) => {
          if (isFilterGroup(clause)) {
            return true; // Keep nested groups for now
          }
          return clause.name !== filterName;
        });
        return {
          ...prev,
          filterGroup: { ...prev.filterGroup, clauses: newClauses },
        };
      });
    },
    [setQueryOptions],
  );
}

/**
 * Filter names that are considered "unique" filters (status)
 * These are handled separately from other advanced filters
 */
const UNIQUE_FILTER_NAMES = new Set<string>([SupportTicketFilterNames.STATUS]);

/**
 * Hook to get/set advanced filters (non-unique filters).
 * This is for filters like assignees, channels, tags, dates, etc.
 */
export function useAdvancedFilters(): {
  filters: AdvancedFilterData[];
  setFilters: Dispatch<SetStateAction<AdvancedFilterData[]>>;
} {
  const { queryOptions, setQueryOptions } = useQueryOptionsState();

  const filters = useMemo(() => {
    const leafFilters = getLeafFilters(queryOptions.filterGroup);
    return leafFilters.filter((f) => !UNIQUE_FILTER_NAMES.has(f.name));
  }, [queryOptions.filterGroup]);

  const setFilters: Dispatch<SetStateAction<AdvancedFilterData[]>> =
    useCallback(
      (action) => {
        setQueryOptions((prev) => {
          const allLeafFilters = getLeafFilters(prev.filterGroup);
          const uniqueFilters = allLeafFilters.filter((f) =>
            UNIQUE_FILTER_NAMES.has(f.name),
          );
          const currentAdvancedFilters = allLeafFilters.filter(
            (f) => !UNIQUE_FILTER_NAMES.has(f.name),
          );
          const newAdvancedFilters =
            typeof action === "function"
              ? action(currentAdvancedFilters)
              : action;
          return {
            ...prev,
            filterGroup: {
              operator: FilterOperator.AND,
              clauses: [...uniqueFilters, ...newAdvancedFilters],
            },
          };
        });
      },
      [setQueryOptions],
    );

  return { filters, setFilters };
}

/**
 * Hook to get the current filter group
 */
export function useFilterGroup(): FilterGroup {
  const queryOptions = useContext(QueryOptionsContext);
  return queryOptions.filterGroup;
}

/**
 * Hook to set the filter group directly
 */
export function useSetFilterGroup(): (filterGroup: FilterGroup) => void {
  const setQueryOptions = useContext(SetQueryOptionsContext);

  return useCallback(
    (filterGroup: FilterGroup) => {
      setQueryOptions((prev) => ({ ...prev, filterGroup }));
    },
    [setQueryOptions],
  );
}
